import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { VendorProductsService } from '../../../services/vendorproducts/vendorproducts.service';
import { CompanyProductSettingMode } from '../companyproductsdialog/companyproductsdialog.component';
import {
  BenefitCategoryTab as PayrollBenefitCategory,
  generatePayrollBenefitSubcategoryId,
  getBenefitCategoryLabel,
  getPayrollBenefitSubcategoryDefinitionsByCategory,
  getPayrollBenefitSubcategoryLabel,
  getPayrollBenefitSubcategoryOptions,
  normalizePayrollBenefitMapping,
  normalizePayrollBenefitSubcategoryDefinitionsByCategory,
  normalizeProductPayrollBenefitMap,
  PAYROLL_BENEFIT_CATEGORY_OPTIONS,
  PayrollBenefitSubcategoryDefinition,
  PayrollBenefitSubcategoryDefinitionsByCategory,
  ProductBenefitMapping as PayrollBenefitMapping,
} from '../../../services/payroll-benefits-assignment/payroll-benefit-categories';

export interface CompanyProductPayrollBenefitCategoriesDialogResult {
  mode: CompanyProductSettingMode;
  productPayrollBenefitCategoriesMap: Record<string, PayrollBenefitMapping>;
  payrollBenefitSubcategoryDefinitionsByCategory: PayrollBenefitSubcategoryDefinitionsByCategory;
}

@Component({
  selector: 'app-company-product-payroll-benefit-categories-dialog',
  templateUrl: './company-product-payroll-benefit-categories-dialog.component.html',
  styleUrls: ['./company-product-payroll-benefit-categories-dialog.component.scss']
})
export class CompanyProductPayrollBenefitCategoriesDialogComponent implements OnInit {
  public items: any[] = [];
  public mode: CompanyProductSettingMode;
  public readonly payrollBenefitCategoryOptions = PAYROLL_BENEFIT_CATEGORY_OPTIONS;
  public readonly subcategoryDraftByCategory = PAYROLL_BENEFIT_CATEGORY_OPTIONS.reduce(
    (result: Record<PayrollBenefitCategory, string>, category) => {
      result[category] = '';
      return result;
    },
    {} as Record<PayrollBenefitCategory, string>
  );
  public subcategoryEditorError = '';

  private effectiveCategoriesMap: Record<string, PayrollBenefitMapping | null> = {};
  private customCategoriesMap: Record<string, PayrollBenefitMapping | null> = {};
  private effectiveSubcategoryDefinitionsByCategory: Record<PayrollBenefitCategory, PayrollBenefitSubcategoryDefinition[]> =
    getPayrollBenefitSubcategoryDefinitionsByCategory();
  private customSubcategoryDefinitionsByCategory: Record<PayrollBenefitCategory, PayrollBenefitSubcategoryDefinition[]> =
    getPayrollBenefitSubcategoryDefinitionsByCategory();

  constructor(
    private vendorProductsService: VendorProductsService,
    public dialogRef: MatDialogRef<CompanyProductPayrollBenefitCategoriesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CompanyProductPayrollBenefitCategoriesDialogModel
  ) {
    this.mode = data?.initialMode || 'custom';
    this.effectiveCategoriesMap = this.normalizeMap(data?.productPayrollBenefitCategoriesMap || {});
    this.customCategoriesMap = this.mode === 'none'
      ? {}
      : this.cloneMap(this.effectiveCategoriesMap);
    this.effectiveSubcategoryDefinitionsByCategory = getPayrollBenefitSubcategoryDefinitionsByCategory(
      data?.payrollBenefitSubcategoryDefinitionsByCategory || {}
    );
    this.customSubcategoryDefinitionsByCategory = this.mode === 'none'
      ? this.buildEmptyDefinitionsByCategory()
      : this.cloneDefinitions(this.effectiveSubcategoryDefinitionsByCategory);
  }

  ngOnInit(): void {
    const selectedProductIds: string[] = Array.isArray(this.data?.products) ? this.data.products : [];
    this.vendorProductsService.getAllVendorProducts().then(products => {
      this.items = (products || []).filter((product: any) => selectedProductIds.includes(product.id));

      const selectedEffectiveMap: Record<string, PayrollBenefitMapping | null> = {};
      const selectedCustomMap: Record<string, PayrollBenefitMapping | null> = {};
      selectedProductIds.forEach((productId: string) => {
        selectedEffectiveMap[productId] = this.effectiveCategoriesMap[productId] || null;
        selectedCustomMap[productId] = this.customCategoriesMap[productId] || null;
      });
      this.effectiveCategoriesMap = selectedEffectiveMap;
      this.customCategoriesMap = selectedCustomMap;
      this.sortItems();
    });
  }

  canInherit(): boolean {
    return !!this.data.canInherit;
  }

  setMode(mode: CompanyProductSettingMode): void {
    const previousMode = this.mode;
    this.mode = mode;
    this.subcategoryEditorError = '';

    if (mode === 'custom' && previousMode !== 'custom' && this.isCustomStateEmpty()) {
      this.customCategoriesMap = this.cloneMap(this.effectiveCategoriesMap);
      this.customSubcategoryDefinitionsByCategory = this.cloneDefinitions(this.effectiveSubcategoryDefinitionsByCategory);
    }

    this.sortItems();
  }

  isCustomMode(): boolean {
    return this.mode === 'custom';
  }

  getMapping(productId: string): PayrollBenefitMapping | null {
    if (this.mode === 'inherit') {
      return this.effectiveCategoriesMap[productId] || null;
    }

    if (this.mode === 'none') {
      return null;
    }

    return this.customCategoriesMap[productId] || null;
  }

  getCategoryLabel(category: PayrollBenefitCategory): string {
    return getBenefitCategoryLabel(category);
  }

  getSubcategoryDefinitions(category: PayrollBenefitCategory): PayrollBenefitSubcategoryDefinition[] {
    if (this.mode === 'inherit') {
      return this.effectiveSubcategoryDefinitionsByCategory[category] || [];
    }

    if (this.mode === 'none') {
      return [];
    }

    return this.customSubcategoryDefinitionsByCategory[category] || [];
  }

  getSubcategoryOptions(category: PayrollBenefitCategory | ''): string[] {
    if (!category) {
      return [];
    }

    return getPayrollBenefitSubcategoryOptions(category, this.getActiveSubcategoryDefinitionsByCategory());
  }

  hasSubcategoryOptions(category: PayrollBenefitCategory | ''): boolean {
    return this.getSubcategoryOptions(category).length > 0;
  }

  getSubcategoryLabel(category: PayrollBenefitCategory | '', subcategoryId: string): string {
    return getPayrollBenefitSubcategoryLabel(category, subcategoryId, this.getActiveSubcategoryDefinitionsByCategory());
  }

  setCategory(productId: string, category: PayrollBenefitCategory | ''): void {
    if (!this.isCustomMode()) {
      return;
    }

    const current = this.customCategoriesMap[productId];
    if (!category) {
      this.customCategoriesMap = {
        ...this.customCategoriesMap,
        [productId]: null
      };
      this.sortItems();
      return;
    }

    const next: PayrollBenefitMapping = { category };
    const options = this.getSubcategoryOptions(category);
    if (current?.subcategory && options.includes(current.subcategory)) {
      next.subcategory = current.subcategory;
    }
    if (current?.requiresApproval === true) {
      next.requiresApproval = true;
    }

    this.customCategoriesMap = {
      ...this.customCategoriesMap,
      [productId]: next
    };
    this.sortItems();
  }

  setSubcategory(productId: string, subcategory: string | ''): void {
    if (!this.isCustomMode()) {
      return;
    }

    const current = this.customCategoriesMap[productId];
    const category = current?.category;
    if (!category) {
      return;
    }

    const options = this.getSubcategoryOptions(category);
    const next: PayrollBenefitMapping = { category };
    if (subcategory && options.includes(subcategory)) {
      next.subcategory = subcategory;
    }
    if (current?.requiresApproval === true) {
      next.requiresApproval = true;
    }

    this.customCategoriesMap = {
      ...this.customCategoriesMap,
      [productId]: next
    };
  }

  updateSubcategoryDraft(category: PayrollBenefitCategory, value: string): void {
    this.subcategoryDraftByCategory[category] = value;
  }

  addSubcategory(category: PayrollBenefitCategory): void {
    if (!this.isCustomMode()) {
      return;
    }

    const label = `${this.subcategoryDraftByCategory[category] || ''}`.trim();
    if (!label) {
      return;
    }

    const definitions = this.getSubcategoryDefinitions(category);
    const existingMatch = definitions.find((definition) => definition.label.toLowerCase() === label.toLowerCase());
    if (existingMatch) {
      this.subcategoryDraftByCategory[category] = '';
      this.subcategoryEditorError = `"${label}" already exists under ${this.getCategoryLabel(category)}.`;
      return;
    }

    const nextDefinition: PayrollBenefitSubcategoryDefinition = {
      id: generatePayrollBenefitSubcategoryId(label, definitions.map((definition) => definition.id)),
      label,
    };
    this.customSubcategoryDefinitionsByCategory = {
      ...this.customSubcategoryDefinitionsByCategory,
      [category]: [...definitions, nextDefinition],
    };
    this.subcategoryDraftByCategory[category] = '';
    this.subcategoryEditorError = '';
  }

  renameSubcategory(category: PayrollBenefitCategory, subcategoryId: string, label: string): void {
    if (!this.isCustomMode()) {
      return;
    }

    const trimmedLabel = `${label || ''}`.trim();
    if (!trimmedLabel) {
      return;
    }

    const currentDefinitions = this.getSubcategoryDefinitions(category);
    const duplicateMatch = currentDefinitions.find((definition) => (
      definition.id !== subcategoryId && definition.label.toLowerCase() === trimmedLabel.toLowerCase()
    ));
    if (duplicateMatch) {
      this.subcategoryEditorError = `"${trimmedLabel}" already exists under ${this.getCategoryLabel(category)}.`;
      return;
    }

    this.customSubcategoryDefinitionsByCategory = {
      ...this.customSubcategoryDefinitionsByCategory,
      [category]: currentDefinitions.map((definition) => (
        definition.id === subcategoryId
          ? { ...definition, label: trimmedLabel }
          : definition
      )),
    };
    this.subcategoryEditorError = '';
  }

  canDeleteSubcategory(category: PayrollBenefitCategory, subcategoryId: string): boolean {
    return !this.isSubcategoryAssigned(category, subcategoryId);
  }

  deleteSubcategory(category: PayrollBenefitCategory, subcategoryId: string): void {
    if (!this.isCustomMode()) {
      return;
    }

    if (!this.canDeleteSubcategory(category, subcategoryId)) {
      this.subcategoryEditorError =
        `Reassign products using ${this.getSubcategoryLabel(category, subcategoryId)} before deleting it.`;
      return;
    }

    this.customSubcategoryDefinitionsByCategory = {
      ...this.customSubcategoryDefinitionsByCategory,
      [category]: this.getSubcategoryDefinitions(category).filter((definition) => definition.id !== subcategoryId),
    };
    this.subcategoryEditorError = '';
  }

  okClicked(): void {
    if (this.mode !== 'custom') {
      this.dialogRef.close({
        mode: this.mode,
        productPayrollBenefitCategoriesMap: {},
        payrollBenefitSubcategoryDefinitionsByCategory: {}
      });
      return;
    }

    const cleanedMap: Record<string, PayrollBenefitMapping> = {};
    Object.entries(this.customCategoriesMap).forEach(([productId, mapping]) => {
      const normalizedMapping = normalizePayrollBenefitMapping(mapping);
      if (normalizedMapping) {
        cleanedMap[productId] = normalizedMapping;
      }
    });

    this.dialogRef.close({
      mode: this.mode,
      productPayrollBenefitCategoriesMap: cleanedMap,
      payrollBenefitSubcategoryDefinitionsByCategory: normalizePayrollBenefitSubcategoryDefinitionsByCategory(
        this.customSubcategoryDefinitionsByCategory
      ),
    });
  }

  onDismiss(): void {
    this.dialogRef.close(undefined);
  }

  private isSubcategoryAssigned(category: PayrollBenefitCategory, subcategoryId: string): boolean {
    return Object.values(this.customCategoriesMap).some((mapping) => (
      mapping?.category === category && mapping?.subcategory === subcategoryId
    ));
  }

  private sortItems(): void {
    this.items = [...this.items].sort((left: any, right: any) => {
      const leftCategory = this.getMapping(`${left?.id || ''}`)?.category || '';
      const rightCategory = this.getMapping(`${right?.id || ''}`)?.category || '';
      const categoryDelta = this.getCategorySortIndex(leftCategory) - this.getCategorySortIndex(rightCategory);
      if (categoryDelta !== 0) {
        return categoryDelta;
      }

      const leftLabel = `${left?.vendorName || ''} ${left?.productName || left?.id || ''}`.trim().toLowerCase();
      const rightLabel = `${right?.vendorName || ''} ${right?.productName || right?.id || ''}`.trim().toLowerCase();
      return leftLabel.localeCompare(rightLabel);
    });
  }

  private getCategorySortIndex(category: PayrollBenefitCategory | '' | undefined): number {
    const normalizedCategory = `${category || ''}`.trim() as PayrollBenefitCategory;
    const categoryIndex = this.payrollBenefitCategoryOptions.indexOf(normalizedCategory);
    return categoryIndex >= 0 ? categoryIndex : Number.MAX_SAFE_INTEGER;
  }

  private normalizeMap(map: Record<string, any>): Record<string, PayrollBenefitMapping | null> {
    const normalized = normalizeProductPayrollBenefitMap(map);
    return Object.keys(map || {}).reduce((result: Record<string, PayrollBenefitMapping | null>, productId) => {
      result[productId] = normalized[productId] || null;
      return result;
    }, {});
  }

  private getActiveSubcategoryDefinitionsByCategory(): Record<PayrollBenefitCategory, PayrollBenefitSubcategoryDefinition[]> {
    if (this.mode === 'inherit') {
      return this.effectiveSubcategoryDefinitionsByCategory;
    }

    if (this.mode === 'none') {
      return this.buildEmptyDefinitionsByCategory();
    }

    return this.customSubcategoryDefinitionsByCategory;
  }

  private buildEmptyDefinitionsByCategory(): Record<PayrollBenefitCategory, PayrollBenefitSubcategoryDefinition[]> {
    return PAYROLL_BENEFIT_CATEGORY_OPTIONS.reduce(
      (result: Record<PayrollBenefitCategory, PayrollBenefitSubcategoryDefinition[]>, category) => {
        result[category] = [];
        return result;
      },
      {} as Record<PayrollBenefitCategory, PayrollBenefitSubcategoryDefinition[]>,
    );
  }

  private isCustomStateEmpty(): boolean {
    const hasMappings = Object.values(this.customCategoriesMap || {}).some((mapping) => !!mapping?.category);
    const hasDefinitions = PAYROLL_BENEFIT_CATEGORY_OPTIONS.some(
      (category) => (this.customSubcategoryDefinitionsByCategory?.[category] || []).length > 0
    );
    return !hasMappings && !hasDefinitions;
  }

  private cloneMap(map: Record<string, PayrollBenefitMapping | null>): Record<string, PayrollBenefitMapping | null> {
    return JSON.parse(JSON.stringify(map || {}));
  }

  private cloneDefinitions(
    definitionsByCategory: Record<PayrollBenefitCategory, PayrollBenefitSubcategoryDefinition[]>,
  ): Record<PayrollBenefitCategory, PayrollBenefitSubcategoryDefinition[]> {
    return getPayrollBenefitSubcategoryDefinitionsByCategory(
      JSON.parse(JSON.stringify(definitionsByCategory || {}))
    );
  }
}

export class CompanyProductPayrollBenefitCategoriesDialogModel {
  constructor(
    public products: string[] = [],
    public productPayrollBenefitCategoriesMap: Record<string, PayrollBenefitMapping> = {},
    public payrollBenefitSubcategoryDefinitionsByCategory: PayrollBenefitSubcategoryDefinitionsByCategory = {},
    public companyName: string = '',
    public canInherit: boolean = false,
    public parentCompanyName: string = '',
    public initialMode: CompanyProductSettingMode = 'custom',
  ) {}
}
